import React, { useEffect, useState } from "react";
import { AlertOctagon, CheckCircle2, Loader2, Lock, PowerOff, ShieldAlert } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Panel del kill-switch de Apify.
 * - GET  /accounts/me/apify  → estado actual.
 * - POST /accounts/me/apify  → toggle (habilitar requiere confirmar=true).
 *
 * UX: rojo por default (deshabilitado), verde cuando está activo, banner
 * grande con timestamp + user que lo activó. Doble confirmación para HABILITAR
 * (modal), un solo click para DESHABILITAR (bajar el freno es siempre seguro).
 */
export default function ApifyKillSwitchPanel() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/accounts/me/apify");
      setState(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el estado de Apify");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doToggle = async (habilitar) => {
    setSaving(true);
    try {
      const r = await api.post("/accounts/me/apify", { habilitar, confirmar: habilitar });
      setState(r.data);
      toast.success(habilitar ? "Apify HABILITADO — cada corrida gasta plata real" : "Apify deshabilitado. Freno activo.");
      setConfirmOpen(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || "Error al cambiar el estado";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (loading) return (
    <section className="border border-slate-200 bg-white p-6" data-testid="apify-panel-loading">
      <Loader2 className="w-4 h-4 animate-spin text-slate-500 inline mr-2" />
      <span className="text-sm text-slate-500">Cargando estado de Apify…</span>
    </section>
  );

  const habilitado = !!state?.habilitado;
  const envLocked = !!state?.env_disabled;
  const canRun = !!state?.can_run;

  return (
    <section
      className={`border-2 p-6 mb-6 transition-colors ${
        canRun ? "border-emerald-600 bg-emerald-50/60"
               : envLocked ? "border-slate-400 bg-slate-100"
                           : "border-red-600 bg-red-50/60"
      }`}
      data-testid="apify-killswitch-panel"
    >
      <div className="flex items-start gap-4">
        <div className={`shrink-0 w-11 h-11 flex items-center justify-center border-2 ${
          canRun ? "border-emerald-700 bg-emerald-100 text-emerald-800"
                 : envLocked ? "border-slate-500 bg-white text-slate-600"
                             : "border-red-700 bg-red-100 text-red-800"
        }`}>
          {canRun ? <CheckCircle2 className="w-6 h-6" />
                  : envLocked ? <Lock className="w-6 h-6" />
                              : <PowerOff className="w-6 h-6" />}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-bold uppercase tracking-[0.18em] mb-1 ${
            canRun ? "text-emerald-800" : envLocked ? "text-slate-600" : "text-red-800"
          }`}>
            Freno de gasto — Apify
          </p>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">
            {canRun ? "Apify HABILITADO — cada corrida cuesta plata real"
                    : envLocked ? "Bloqueado por entorno (APIFY_DISABLED=1)"
                                : "Apify deshabilitado — nadie puede disparar corridas"}
          </h2>
          <p className="font-body text-sm text-slate-700">
            {canRun
              ? "Un admin habilitó Apify. Toda captura (manual o programada) va a llamar a Zonaprop / Properati y consumir crédito. Desactivá cuando termines."
              : envLocked
                ? "El proceso backend tiene APIFY_DISABLED=1 (nivel deploy). Ni el toggle admin puede destrabarlo. Sacá esa env var y reiniciá el backend para poder habilitar via UI."
                : "Por defecto no se puede disparar Apify. Activá SOLO cuando Gonzalo autorice una corrida específica; después volvé a apagar."
            }
          </p>

          {(state?.habilitado_at || state?.deshabilitado_at) && (
            <div className="mt-3 text-[11px] font-mono text-slate-500 border-l-2 border-slate-300 pl-3 space-y-0.5">
              {state.habilitado_at && (
                <div data-testid="apify-audit-habilitado">
                  Última habilitación: <strong>{fmtDate(state.habilitado_at)}</strong> · por {state.habilitado_by || "?"}
                </div>
              )}
              {state.deshabilitado_at && (
                <div data-testid="apify-audit-deshabilitado">
                  Última deshabilitación: <strong>{fmtDate(state.deshabilitado_at)}</strong> · por {state.deshabilitado_by || "?"}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {habilitado ? (
              <Button
                data-testid="apify-btn-deshabilitar"
                onClick={() => doToggle(false)}
                disabled={saving}
                className="bg-red-700 hover:bg-red-800 text-white rounded-none h-11 px-6"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PowerOff className="w-4 h-4 mr-2" />}
                Deshabilitar Apify
              </Button>
            ) : (
              <Button
                data-testid="apify-btn-habilitar"
                onClick={() => setConfirmOpen(true)}
                disabled={saving || envLocked}
                className={`rounded-none h-11 px-6 ${envLocked ? "bg-slate-400 cursor-not-allowed" : "bg-emerald-700 hover:bg-emerald-800 text-white"}`}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
                {envLocked ? "Bloqueado por entorno" : "Habilitar Apify (con confirmación)"}
              </Button>
            )}
            <span className={`text-xs font-mono px-2 py-1 border ${
              canRun ? "border-emerald-700 text-emerald-800 bg-white"
                     : "border-red-700 text-red-800 bg-white"
            }`} data-testid="apify-state-badge">
              can_run = {String(canRun)}
            </span>
          </div>
        </div>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmOpen(false)}
          data-testid="apify-confirm-modal"
        >
          <div
            className="bg-white border-2 border-red-600 p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertOctagon className="w-6 h-6 text-red-700 shrink-0" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-red-800 mb-1">
                  Confirmar habilitación
                </p>
                <h3 className="font-heading text-lg font-bold text-slate-900">
                  ¿Habilitar Apify? Cada corrida cuesta plata real.
                </h3>
              </div>
            </div>
            <ul className="text-sm text-slate-700 space-y-1.5 mb-5 pl-1">
              <li>· Zonaprop ≈ US$ 0.05–0.10 por corrida (memo23).</li>
              <li>· Properati ≈ US$ 0.20 por corrida (unfenced-group).</li>
              <li>· Se cobra por cada zona × cada fuente por corrida.</li>
              <li>· Acordate de <strong>volver a deshabilitar</strong> cuando termines.</li>
            </ul>
            <div className="flex gap-3 justify-end">
              <Button
                data-testid="apify-confirm-cancel"
                onClick={() => setConfirmOpen(false)}
                variant="outline"
                className="rounded-none h-10"
              >Cancelar</Button>
              <Button
                data-testid="apify-confirm-yes"
                onClick={() => doToggle(true)}
                disabled={saving}
                className="bg-red-700 hover:bg-red-800 text-white rounded-none h-10 px-5"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Sí, habilitar y gastar
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}
